import React from "react";
import { Link } from "react-router-dom";

// CategoryBadge Component for reusability and dynamic styling
const CategoryBadge = ({ category, color }) => {
  const badgeStyle = {
    backgroundColor: color,
    padding: "5px 10px",
    borderRadius: "5px",
    marginRight: "10px",
    fontSize: "0.75rem",
    fontWeight: "700",
    color: "#fff",
  };

  return <div style={badgeStyle}>{category}</div>;
};

export default function BlogPageBox4() {
  return (
    <div className="blog-page-parent">
      {/* Date and Author Section */}
      <div className="blog-page-date-author">
        <span>Published</span>
        <span className="blog-page-date"> January 30, 2025</span>
        <span> by</span>
        <span className="blog-page-author"> FlexNet Team</span>
      </div>

      {/* Title Section */}
      <div className="blog-page-title">
        FlexNet Marketplace: Driving Innovation in Connectivity Solutions
      </div>

      {/* Category Badges */}
      <div className="blog-page-categories">
        {[
          { name: "5G Technology", color: "#6366F1" },
          { name: "Enterprise Solutions", color: "#22C55E" },
          { name: "IoT & Connectivity", color: "#F59E0B" },
        ].map((category, index) => (
          <CategoryBadge
            key={index}
            category={category.name}
            color={category.color}
          />
        ))}
      </div>

      {/* Blog Content */}
      <div className="blog-page-info">
        <h2>Introduction</h2>
        <p>
          Innovation thrives when the right tools are within reach. The FlexNet
          Marketplace is a curated hub of applications, devices, and solutions
          seamlessly accessible through the FlexNet GUI. Designed exclusively
          for FlexNet customers, the Marketplace ensures direct access to
          industry-relevant tools that enhance the performance and capabilities
          of private 4G/5G networks.
        </p>

        <h2>What is the FlexNet Marketplace?</h2>
        <p>
          The FlexNet Marketplace is an integrated feature within the FlexNet
          GUI, offering exclusive access to a range of solutions tailored to
          meet your operational challenges. While not an open online platform,
          the Marketplace is purpose-built for FlexNet users to explore and
          deploy tools and applications that fit their unique requirements.
        </p>

        <h2>What You Can Find in the Marketplace</h2>
        <h3 className="font-bold">1. Applications for Every Industry:</h3>
        <ul>
          <li>IoT management platforms for real-time monitoring.</li>
          <li>AI-powered analytics to optimize workflows.</li>
          <li>
            Worker safety and productivity tools leveraging wearable technology.
          </li>
        </ul>

        <h3 className="font-bold">2. Devices for Seamless Connectivity:</h3>
        <ul>
          <li>4G/5G-ready routers, gateways, and repeaters.</li>
          <li>
            Specialized IoT sensors for industrial, logistics, or construction
            needs.
          </li>
          <li>
            Wearables like smart helmets, AR/VR devices, and smart cameras.
          </li>
        </ul>

        <h3 className="font-bold">3. Customizable Solutions:</h3>
        <ul>
          <li>
            Explore tools designed for specific verticals or create solutions
            tailored to your operational challenges.
          </li>
          <li>
            From manufacturing floors to broadcast studios, the Marketplace
            adapts to your needs.
          </li>
        </ul>

        <h2>How the FlexNet Marketplace Drives Value</h2>
        <ul>
          <li>
            <strong>Exclusive Access Through FlexNet GUI:</strong> Only FlexNet
            customers can directly access the Marketplace, ensuring tailored
            solutions designed for private 4G/5G environments.
          </li>
          <li>
            <strong>Accelerated Innovation:</strong> The Marketplace offers
            cutting-edge tools and technologies to help you stay ahead in your
            industry.
          </li>
          <li>
            <strong>Plug-and-Play Compatibility:</strong> Every app and device
            integrates effortlessly with your existing FlexNet network,
            simplifying adoption and use.
          </li>
          <li>
            <strong>Scalability for Growth:</strong> Easily add new capabilities
            as your business evolves, ensuring your network grows with you.
          </li>
        </ul>

        <h2>Who Benefits from the FlexNet Marketplace?</h2>
        <p>
          The FlexNet Marketplace is designed to support businesses across all
          industries, ensuring every customer finds the tools they need. Some
          examples include:
        </p>
        <ul>
          <li>
            <strong>Manufacturers:</strong> Boost productivity with
            IoT-connected machinery, predictive maintenance solutions, and AR/VR
            tools for training.
          </li>
          <li>
            <strong>Logistics Providers:</strong> Enhance real-time tracking,
            inventory management, and fleet monitoring with industry-specific
            apps and devices.
          </li>
          <li>
            <strong>Construction Firms:</strong> Keep worksites connected with
            wearables for safety, monitoring drones, and smart tools for data
            collection.
          </li>
          <li>
            <strong>Broadcasters:</strong> Ensure seamless live streaming and
            content delivery with robust connectivity devices and media
            management apps.
          </li>
          <li>
            <strong>Retailers:</strong> Leverage customer analytics and
            AI-driven tools to improve in-store experiences and operational
            efficiency.
          </li>
          <li>
            <strong>Healthcare Facilities:</strong> Use secure, real-time
            communication tools for remote monitoring and improved patient care.
          </li>
          <li>
            <strong>Energy Providers:</strong> Optimize energy grids, monitor
            renewable sources, and ensure operational continuity with
            IoT-enabled solutions.
          </li>
          <li>
            <strong>Smart Cities and Governments:</strong> Enable public safety,
            traffic management, and infrastructure monitoring with advanced
            connectivity tools.
          </li>
        </ul>

        <h2>Why Choose the FlexNet Marketplace?</h2>
        <ul>
          <li>
            <strong>Industry-Specific Tools:</strong> Access solutions designed
            to solve your unique challenges.
          </li>
          <li>
            <strong>Integrated Access:</strong> Easily explore and deploy tools
            directly through the FlexNet GUI.
          </li>
          <li>
            <strong>Future-Ready Platform:</strong> Add new applications and
            devices as your business grows.
          </li>
          <li>
            <strong>Trusted Ecosystem:</strong> All solutions are tested for
            compatibility with FlexNet private networks.
          </li>
        </ul>

        <h2>Explore the FlexNet Marketplace Today</h2>
        <p>
          The future of connectivity starts with the tools that drive it.
          FlexNet Marketplace is your gateway to innovation, available directly
          through the FlexNet GUI.
        </p>

        <h2>Contact Us to Learn More</h2>

        <p className="font-bold text-lg">
          <Link to="/contact-us">
            <span className="text-blue-600 mr-1">Contact Us</span>
          </Link>
          or
          <Link to="/connectivity">
            <span className="text-blue-600 mr-1 ml-1">Request a Demo</span> to
            experience how the FlexNet Marketplace can transform your business.
          </Link>
        </p>
      </div>
    </div>
  );
}
